/**
 * Theme → CSS. Turns the funnel's `theme` config into CSS custom properties
 * and injects the engine's base stylesheet. All engine markup is namespaced
 * under `.fe-` so it never collides with a host page's styles.
 */
import type { Theme } from "../types";

function themeVars(theme: Theme): string {
  const p = theme.palette;
  const t = theme.typography;
  const b = theme.button_style;
  return [
    `--fe-primary:${p.primary}`,
    `--fe-on-primary:${p.on_primary}`,
    `--fe-bg:${p.background}`,
    `--fe-surface:${p.surface}`,
    `--fe-text:${p.text}`,
    `--fe-text-on-surface:${p.text_on_surface ?? p.text}`,
    `--fe-muted:${p.muted}`,
    `--fe-muted-on-surface:${p.muted_on_surface ?? p.muted}`,
    `--fe-border:${p.border}`,
    `--fe-success:${p.success}`,
    `--fe-error:${p.error}`,
    `--fe-accent:${p.accent ?? p.primary}`,
    `--fe-font:${t.font_family}`,
    `--fe-size:${t.base_size_px ?? 17}px`,
    `--fe-heading-weight:${t.heading_weight ?? 700}`,
    `--fe-radius:${b.radius_px ?? 10}px`,
  ].join(";");
}

/** CSS-var-name lookup. Maps `dark_palette` overrides to the same vars `themeVars()` writes, so a dark_palette entry overrides only the named token via `@media (prefers-color-scheme: dark)`. */
const PALETTE_VAR_NAMES: Record<string, string> = {
  primary: "--fe-primary",
  on_primary: "--fe-on-primary",
  background: "--fe-bg",
  surface: "--fe-surface",
  text: "--fe-text",
  text_on_surface: "--fe-text-on-surface",
  muted: "--fe-muted",
  muted_on_surface: "--fe-muted-on-surface",
  border: "--fe-border",
  success: "--fe-success",
  error: "--fe-error",
  accent: "--fe-accent",
};

/** Emit a `@media (prefers-color-scheme: dark)` block overriding only the palette keys present in `dark_palette`. Returns empty string when none are set. */
function darkPaletteCss(theme: Theme): string {
  const d = theme.dark_palette;
  if (!d) return "";
  const overrides: string[] = [];
  for (const [key, value] of Object.entries(d)) {
    if (value === undefined) continue;
    const varName = PALETTE_VAR_NAMES[key];
    if (!varName) continue;
    overrides.push(`${varName}:${value}`);
  }
  if (overrides.length === 0) return "";
  return `\n@media (prefers-color-scheme: dark){.fe-root{${overrides.join(";")}}}`;
}

const BASE_CSS = `
.fe-root{font-family:var(--fe-font);font-size:var(--fe-size);color:var(--fe-text);
  background:var(--fe-bg);min-height:100vh;line-height:1.5;
  -webkit-font-smoothing:antialiased;box-sizing:border-box}
.fe-root *,.fe-root *::before,.fe-root *::after{box-sizing:inherit}
.fe-shell{max-width:620px;margin:0 auto;padding:14px 18px 56px;display:flex;
  flex-direction:column;gap:14px}
.fe-logo{max-height:40px;align-self:center}
.fe-logo-mark{align-self:center;display:flex;align-items:center;gap:9px}
.fe-logo-icon{display:flex;width:38px;height:38px;flex:none}
.fe-logo-icon svg{display:block;width:100%;height:100%}
.fe-logo-words{display:flex;flex-direction:column;line-height:1}
.fe-logo-text{font-weight:800;font-size:1.34em;letter-spacing:-.01em;color:var(--fe-primary)}
.fe-logo-tagline{font-size:.6em;font-weight:700;letter-spacing:.19em;
  text-transform:uppercase;color:var(--fe-muted);margin-top:3px}
.fe-progress{height:6px;background:var(--fe-border);border-radius:99px;overflow:hidden}
.fe-progress-fill{height:100%;background:var(--fe-primary);transition:width .35s ease}
.fe-card{background:var(--fe-surface);border:1px solid var(--fe-border);
  border-radius:var(--fe-radius);padding:18px;color:var(--fe-text-on-surface)}
.fe-card .fe-text{color:var(--fe-text-on-surface)}
.fe-card .fe-muted{color:var(--fe-muted-on-surface)}
.fe-card--value{border-color:var(--fe-accent)}
.fe-card--warning{border-color:var(--fe-error)}
.fe-card--success{border-color:var(--fe-success)}
.fe-h{font-weight:var(--fe-heading-weight);line-height:1.25;margin:0}
.fe-h1{font-size:1.6em;color:var(--fe-accent)}.fe-h2{font-size:1.3em}.fe-h3{font-size:1.1em}
.fe-text{margin:0;color:var(--fe-text)}
.fe-muted{color:var(--fe-muted)}
.fe-img{display:block;border-radius:var(--fe-radius);max-width:100%}
.fe-divider{border:0;border-top:1px solid var(--fe-border);margin:4px 0}
.fe-list{margin:0;padding-left:0;list-style:none;display:flex;flex-direction:column;gap:8px}
.fe-list--bullet{padding-left:20px;list-style:disc}
.fe-list--number{padding-left:20px;list-style:decimal}
.fe-list-check{display:flex;gap:8px;align-items:flex-start}
.fe-list-check::before{content:"✓";color:var(--fe-success);font-weight:700}
.fe-field{display:flex;flex-direction:column;gap:8px}
.fe-label{font-weight:600}
.fe-help{font-size:.85em;color:var(--fe-muted)}
.fe-error{font-size:.85em;color:var(--fe-error)}
.fe-zip-hint{font-size:.9em;color:var(--fe-success);font-weight:600}
.fe-input,.fe-select,.fe-textarea{font:inherit;color:var(--fe-text-on-surface);background:var(--fe-surface);
  border:1px solid var(--fe-border);border-radius:var(--fe-radius);padding:13px 14px;width:100%}
.fe-input:focus,.fe-select:focus,.fe-textarea:focus{outline:2px solid var(--fe-primary);
  outline-offset:1px;border-color:var(--fe-primary)}
.fe-input--error,.fe-select--error,.fe-textarea--error{border-color:var(--fe-error)}
.fe-combobox{position:relative}
.fe-combobox-list{position:absolute;z-index:20;left:0;right:0;top:calc(100% + 4px);
  margin:0;padding:4px;list-style:none;background:var(--fe-surface);
  border:1px solid var(--fe-border);border-radius:var(--fe-radius);
  max-height:220px;overflow-y:auto;box-shadow:0 6px 20px rgba(0,0,0,.12)}
.fe-combobox-option{padding:10px 12px;border-radius:6px;cursor:pointer}
.fe-combobox-option:hover{background:color-mix(in srgb,var(--fe-primary) 10%,var(--fe-surface))}
.fe-combobox-option--active{background:color-mix(in srgb,var(--fe-primary) 14%,var(--fe-surface));font-weight:600}
.fe-combobox-empty{padding:10px 12px;color:var(--fe-muted-on-surface)}
.fe-choices{display:flex;flex-direction:column;gap:10px}
.fe-choice{display:flex;align-items:center;gap:12px;text-align:left;width:100%;
  font:inherit;color:var(--fe-text-on-surface);background:var(--fe-surface);cursor:pointer;
  border:1.5px solid var(--fe-border);border-radius:var(--fe-radius);padding:15px 16px;
  transition:border-color .12s ease,background .12s ease}
.fe-choice:hover{border-color:var(--fe-primary)}
.fe-choice--selected{border-color:var(--fe-primary);background:color-mix(in srgb,var(--fe-primary) 8%,var(--fe-surface))}
.fe-choices--tiles{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.fe-tile{flex-direction:column;text-align:center;gap:8px;padding:18px 12px}
.fe-tile-img{width:100%;height:96px;object-fit:cover;border-radius:8px}
.fe-choice-icon{font-size:1.85em;line-height:1;width:1.5em;flex:none;text-align:center}
.fe-choice-label{flex:1}
.fe-icon{display:inline-flex;vertical-align:-.15em}
.fe-icon svg{display:block}
.fe-choice-badge{display:inline-flex;align-items:center;justify-content:center;
  width:42px;height:42px;flex:none;border-radius:9px;
  background:color-mix(in srgb,var(--fe-primary) 12%,var(--fe-surface));
  color:var(--fe-primary)}
.fe-tile .fe-choice-badge{width:54px;height:54px}
.fe-steps{display:flex;align-items:flex-start;justify-content:center;padding:2px 0 6px}
.fe-step{display:flex;flex-direction:column;align-items:center;gap:6px;flex:none;width:82px}
.fe-step-dot{display:flex;align-items:center;justify-content:center;width:28px;height:28px;
  border-radius:50%;font-size:.82em;font-weight:700;border:2px solid var(--fe-border);
  background:var(--fe-surface);color:var(--fe-muted-on-surface)}
.fe-step--active .fe-step-dot,.fe-step--done .fe-step-dot{border-color:var(--fe-primary);
  background:var(--fe-primary);color:var(--fe-on-primary)}
.fe-step-label{font-size:.8em;color:var(--fe-muted);text-align:center}
.fe-step--active .fe-step-label{color:var(--fe-text);font-weight:600}
.fe-steps-line{flex:1;max-width:44px;height:2px;margin-top:13px;background:var(--fe-border)}
.fe-steps-line--done{background:var(--fe-primary)}
.fe-accordion{display:flex;flex-direction:column;gap:8px}
.fe-acc-item{border:1px solid var(--fe-border);border-radius:var(--fe-radius);
  background:var(--fe-surface);overflow:hidden;color:var(--fe-text-on-surface)}
.fe-acc-item--open{border-color:var(--fe-primary)}
.fe-acc-q{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;
  text-align:left;font:inherit;font-weight:600;color:inherit;background:none;border:0;
  cursor:pointer;padding:15px 16px}
.fe-acc-mark{flex:none;font-size:1.4em;line-height:1;color:var(--fe-primary);font-weight:700}
.fe-acc-a{padding:0 16px 15px;color:var(--fe-muted-on-surface)}
.fe-consent{display:flex;gap:10px;align-items:flex-start;cursor:pointer}
.fe-consent input{margin-top:3px;width:18px;height:18px;flex:none}
.fe-consent-text{font-size:.85em;color:var(--fe-text)}
.fe-btn{font:inherit;font-weight:700;cursor:pointer;border:0;
  border-radius:var(--fe-radius);padding:15px 20px;
  background:var(--fe-primary);color:var(--fe-on-primary);width:100%}
.fe-btn:disabled{opacity:.5;cursor:not-allowed}
.fe-btn--ghost{background:transparent;color:var(--fe-primary);font-weight:600;
  padding:13px 18px;width:100%;border:1.5px solid var(--fe-border)}
.fe-btn--ghost:hover{border-color:var(--fe-primary);
  background:color-mix(in srgb,var(--fe-primary) 5%,var(--fe-surface))}
.fe-nav{display:flex;flex-direction:column;gap:8px;margin-top:4px}
.fe-fade{animation:fe-fade .25s ease both}
@keyframes fe-fade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
@media(min-width:560px){.fe-shell{padding-top:22px}}
@media (prefers-color-scheme: dark){
  /* Logo SVG navy fills rotate down the brand palette so the mark reads on dark bg:
     deepest navy -> mid-blue (matches the wordmark's "True"); mid-navy -> white. */
  .fe-logo-icon svg polygon[fill="#0B1B2B"],
  .fe-logo-icon svg circle[fill="#0B1B2B"]{fill:#2F6B8F !important}
  .fe-logo-icon svg polygon[fill="#1C3A55"]{fill:#ffffff !important}
  /* Wordmark — Case (was light-blue) goes white; True (was deep navy) takes Case's light-blue.
     !important is required because the inline style attribute always wins specificity
     otherwise. Position-based nth-child as a defensive fallback. */
  .fe-logo-text > span:nth-child(1){color:#2F6B8F !important}
  .fe-logo-text > span:nth-child(2){color:#ffffff !important}
}
`;

let injected = false;

/** Inject the engine stylesheet + the funnel's theme variables. Idempotent. */
export function injectStyles(theme: Theme): void {
  if (injected) return;
  injected = true;
  const style = document.createElement("style");
  style.setAttribute("data-funnel-engine", "");
  style.textContent = `.fe-root{${themeVars(theme)}}\n${BASE_CSS}${darkPaletteCss(theme)}`;
  document.head.appendChild(style);
}
